import classNames from 'classnames';
import { useField } from 'formik';
import { FlexGrid, useModal } from 'yoso';
import { ContentSelectFoggyBox } from '~/components/Box/Foggy/Select/ContentSelectFoggyBox';
import { PlaceholderCard } from '~/components/Card/Placeholder';
import { EvolveValues } from '~/components/Evolve/Form/EvolveForm.hooks';
import styles from '~/components/Evolve/Select/Field/EvolveSelectField.module.scss';
import { FriendNftContent } from '~/components/Friend/Content/FriendNftContent';
import { PlaceholderGrid } from '~/components/Grid/Placeholder/PlaceholderGrid';
import { t } from '~/components/I18n';
import { AdditionalNftModel, useFriendsByType } from '~/components/Membership/Membership.hooks';
import { Alert } from '~/components/Modal/Alert/Alert';
import { NftCheckbox } from '~/components/Nft/Checkbox/NftCheckbox';

export type EvolveSelectFieldProps = {
  name: string;
  tokenType: AdditionalNftModel['tokenType'];
  className?: string;
};

export const EvolveSelectField = ({ name, tokenType, className }: EvolveSelectFieldProps) => {
  const { data: friends = [] } = useFriendsByType(tokenType);
  const { open } = useModal();

  const [{ value }, , helpers] = useField<EvolveValues['tokenIdList']>(name);
  const { setValue } = helpers;

  // setValue reference is keep changing so no need for useCallback (formik issue https://github.com/jaredpalmer/formik/issues/3602)
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const tokenId = event.currentTarget.value;
    const newValue = [...value];
    if (value.includes(tokenId)) {
      const targetIndex = value.indexOf(tokenId);
      newValue[targetIndex] = undefined;
    } else {
      const undefinedIndex = value.indexOf(undefined);
      const targetIndex = undefinedIndex === -1 ? newValue.length : undefinedIndex;
      newValue[targetIndex] = tokenId;
    }

    if (newValue.length > 3) {
      open(
        <Alert
          description={t('citizen_evolve_sec1_popup_desc')}
          confirmText={t('citizen_confirm_ok')}
        />
      );
      return;
    }
    setValue(newValue);
  };

  return (
    <ContentSelectFoggyBox
      className={classNames(styles.root, className)}
      contentLength={friends.length}>
      <PlaceholderGrid
        className={styles.list}
        placeholder={<PlaceholderCard type="face" className={styles.placeholder} />}>
        {friends.map((friend) => (
          <FlexGrid className={styles.item} key={friend.id}>
            <NftCheckbox
              value={friend.id}
              onChange={handleChange}
              checked={value.includes(friend.id)}
              title={value[0] === friend.id ? t('citizen_evolve_unit_button_main') : undefined}>
              <FriendNftContent
                {...friend}
                className={styles.nftContent}
                size="small"
                title={null}
              />
            </NftCheckbox>
          </FlexGrid>
        ))}
      </PlaceholderGrid>
    </ContentSelectFoggyBox>
  );
};
